import { useState, useEffect } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/cms/ui/dialog';
import LureoButton from '@/common/LureoButton';
import { Input } from '@/components/cms/ui/input';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { toast } from 'sonner';
import { OrgInstance } from '@/types/orgTypes';

interface VoucherRedeemModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback to refresh org data
}

export function VoucherRedeemModal({
  open,
  onClose,
  onSuccess
}: VoucherRedeemModalProps) {
  const services = useServices();
  const { user } = useAuthUser();
  const [orgInstance, setOrgInstance] = useState<OrgInstance | null>(null);
  const [voucherCode, setVoucherCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // The org is read when the modal opens, not while it sits closed
  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;
    services.organization
      .fetchOrgInstanceByUserId(user.id)
      .then(org => {
        if (!cancelled) setOrgInstance(org);
      })
      .catch(error => console.error('Error loading the organization:', error));
    return () => {
      cancelled = true;
    };
  }, [open, user?.id, services.organization]);

  const handleSubmit = async () => {
    if (!voucherCode.trim()) {
      setError('Please enter a voucher code');
      return;
    }

    if (!orgInstance?.orgInstanceId) {
      setError('Organization not found. Please try again.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await services.billing.redeemVoucher({
        voucherCode: voucherCode.trim(),
        orgInstanceId: orgInstance.orgInstanceId
      });

      // Success!
      toast.success(`Voucher redeemed! Upgraded to ${result.subscriptionPlan} plan`);

      // Clear the input
      setVoucherCode('');

      // Call success callback to refresh org data
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (err) {
      // Error is already handled by billingService (toast shown)
      setError(err instanceof Error ? err.message : 'Failed to redeem voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVoucherCode('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redeem Voucher</DialogTitle>
          <DialogDescription>
            Enter your voucher code to upgrade your plan
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div>
            <Input
              placeholder="Enter voucher code"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && !loading && handleSubmit()}
              className="text-center text-lureo-18 tracking-wider leading-7"
              disabled={loading}
            />
            {error && (
              <p className="mt-2 text-center text-lureo-12 text-lureo-danger-ink">{error}</p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <LureoButton
            onClick={handleClose}
            variant="outline-3"
            suppressTooltip
            disabled={loading}
            className="h-9 px-5 rounded-lureo-radius-button-pill"
          >
            Cancel
          </LureoButton>
          <LureoButton
            onClick={handleSubmit}
            disabled={loading || !voucherCode.trim()}
            variant="secondary"
            suppressTooltip
            className="h-9 px-4 rounded-lureo-radius-button-pill"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" tone="on-cta" />
                Redeeming...
              </>
            ) : (
              'Redeem Voucher'
            )}
          </LureoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

